// // ============================================
// // STEP 1: PERSONAL DETAILS
// // ============================================

// import { useState, useEffect } from 'react';
// import { ArrowRight, MessageCircle } from 'lucide-react';
// import {
//   Card,
//   CardContent,
//   CardHeader,
//   CardTitle,
//   CardDescription,
//   CardFooter,
//   Button,
//   Input,
//   SelectionCard,
//   Toast,
// } from '../../../components/ui';
// import { useFormStore } from '../../../store';
// import { AGE_OPTIONS, PERSONA_OPTIONS } from '../../../constants';
// import styles from './Steps.module.css';

// interface ValidationErrors {
//   name?: string;
//   email?: string;
//   phone?: string;
//   location?: string;
//   whatsappNumber?: string;
// }

// export function PersonalDetailsStep(): JSX.Element {
//   useEffect(() => {
//     window.scrollTo({ top: 0, behavior: 'auto' });
//   }, []);

//   const { formData, updatePersonalDetails, nextStep } = useFormStore();
//   const { personalDetails } = formData;

//   const [errors, setErrors] = useState<ValidationErrors>({});
//   const [touched, setTouched] = useState<Record<string, boolean>>({});
//   const [toast, setToast] = useState('');

//   const validateField = (field: string, value: string): string | undefined => {
//     switch (field) {
//       case 'name':
//         if (value.trim().length < 2) return 'Please enter your full name';
//         return undefined;
//       case 'email':
//         if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email';
//         return undefined;
//       case 'phone':
//         const cleanPhone = value.replace(/\D/g, '');
//         if (!/^[6-9]\d{9}$/.test(cleanPhone)) return 'Please enter a valid 10-digit mobile number';
//         return undefined;
//       case 'location':
//         if (value.trim().length < 2) return 'Please enter your city or location';
//         return undefined;
//       case 'whatsappNumber':
//         if (!personalDetails.whatsappSameAsPhone && value) {
//           const cleanWa = value.replace(/\D/g, '');
//           if (!/^[6-9]\d{9}$/.test(cleanWa)) return 'Please enter a valid 10-digit WhatsApp number';
//         }
//         return undefined;
//       default:
//         return undefined;
//     }
//   };

//   const handleChange = (field: keyof typeof personalDetails, value: string | boolean) => {
//     updatePersonalDetails({ [field]: value });
//     if (touched[field as string] && typeof value === 'string') {
//       setErrors(prev => ({ ...prev, [field]: validateField(field as string, value) }));
//     }
//   };

//   const handleBlur = (field: string) => {
//     setTouched(prev => ({ ...prev, [field]: true }));
//     const value = personalDetails[field as keyof typeof personalDetails];
//     if (typeof value === 'string') {
//       setErrors(prev => ({ ...prev, [field]: validateField(field, value) }));
//     }
//   };

//   const handleNext = () => {
//     const newErrors: ValidationErrors = {
//       name: validateField('name', personalDetails.name),
//       email: validateField('email', personalDetails.email),
//       phone: validateField('phone', personalDetails.phone),
//       location: validateField('location', personalDetails.location),
//       whatsappNumber: !personalDetails.whatsappSameAsPhone
//         ? validateField('whatsappNumber', personalDetails.whatsappNumber || '')
//         : undefined,
//     };

//     setErrors(newErrors);
//     setTouched({ name: true, email: true, phone: true, location: true, whatsappNumber: true });

//     if (!Object.values(newErrors).some(error => error)) {
//       nextStep();
//     } else {
//       const firstError = Object.values(newErrors).find(e => e);
//       if (firstError) setToast(firstError);
//     }
//   };

//   return (
//     <>
//       <Toast message={toast} onClose={() => setToast('')} />
//       <Card variant="elevated">
//         <CardHeader>
//           <CardTitle>Let's start with the basics</CardTitle>
//           <CardDescription>Tell us about yourself so we can personalize your recommendation.</CardDescription>
//         </CardHeader>
//         <CardContent>
//           <div className={styles.formGrid}>
//             <Input
//               label="Full Name"
//               placeholder="Enter your name"
//               value={personalDetails.name}
//               onChange={(e) => handleChange('name', e.target.value)}
//               onBlur={() => handleBlur('name')}
//               error={touched.name ? errors.name : undefined}
//             />
//             <Input
//               label="Email Address"
//               type="email"
//               placeholder="you@example.com"
//               value={personalDetails.email}
//               onChange={(e) => handleChange('email', e.target.value)}
//               onBlur={() => handleBlur('email')}
//               error={touched.email ? errors.email : undefined}
//               disabled
//               hint="Verified from login"
//             />
//             <Input
//               label="Mobile Number"
//               type="tel"
//               placeholder="10-digit mobile number"
//               value={personalDetails.phone}
//               onChange={(e) => handleChange('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
//               onBlur={() => handleBlur('phone')}
//               error={touched.phone ? errors.phone : undefined}
//             />
//             <Input
//               label="City / Location"
//               placeholder="e.g., Mumbai, Delhi, Bangalore"
//               value={personalDetails.location}
//               onChange={(e) => handleChange('location', e.target.value)}
//               onBlur={() => handleBlur('location')}
//               error={touched.location ? errors.location : undefined}
//             />
//           </div>

//           <div className={styles.sectionTitle}>
//             <h4>Your Age Group</h4>
//             <p className={styles.sectionSubtitle}>Helps us understand your context better</p>
//           </div>
//           <div className={styles.optionsRow}>
//             {AGE_OPTIONS.map(option => (
//               <button
//                 key={option.value}
//                 type="button"
//                 className={`${styles.chipButton} ${personalDetails.age === option.value ? styles.chipActive : ''}`}
//                 onClick={() => handleChange('age', option.value)}
//               >
//                 {option.label}
//               </button>
//             ))}
//           </div>

//           <div className={styles.sectionTitle}>
//             <h4>Who are you?</h4>
//             <p className={styles.sectionSubtitle}>Your primary role or occupation</p>
//           </div>
//           <div className={styles.optionsGrid}>
//             {PERSONA_OPTIONS.map(option => (
//               <SelectionCard
//                 key={option.value}
//                 selected={personalDetails.occupation === option.value}
//                 onClick={() => handleChange('occupation', option.value)}
//                 icon={<span className={styles.iconEmoji}>{option.icon}</span>}
//                 title={option.label}
//               />
//             ))}
//           </div>

//           <div className={styles.sectionTitle}>
//             <h4>
//               <MessageCircle size={18} className={styles.inlineIcon} />
//               WhatsApp Contact
//             </h4>
//             <p className={styles.sectionSubtitle}>We'll send your personalized recommendation via WhatsApp</p>
//           </div>

//           <div className={styles.checkboxRow}>
//             <label className={styles.checkboxLabel}>
//               <input
//                 type="checkbox"
//                 checked={personalDetails.whatsappSameAsPhone}
//                 onChange={(e) => handleChange('whatsappSameAsPhone', e.target.checked)}
//               />
//               <span>WhatsApp number is same as mobile number</span>
//             </label>
//           </div>

//           {!personalDetails.whatsappSameAsPhone && (
//             <div className={styles.conditionalField}>
//               <Input
//                 label="WhatsApp Number"
//                 type="tel"
//                 placeholder="10-digit WhatsApp number"
//                 value={personalDetails.whatsappNumber || ''}
//                 onChange={(e) => handleChange('whatsappNumber', e.target.value.replace(/\D/g, '').slice(0, 10))}
//                 onBlur={() => handleBlur('whatsappNumber')}
//                 error={touched.whatsappNumber ? errors.whatsappNumber : undefined}
//               />
//             </div>
//           )}
//         </CardContent>
//         <CardFooter>
//           <Button onClick={handleNext} size="lg">
//             Continue
//             <ArrowRight size={18} />
//           </Button>
//         </CardFooter>
//       </Card>
//     </>
//   );
// }
// ============================================
// STEP 1: PERSONAL DETAILS
// ============================================

import { useState, useEffect } from 'react';
import { ArrowRight, MessageCircle } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
  Button,
  Input,
  SelectionCard,
} from '../../../components/ui';
import { useFormStore } from '../../../store';
import { AGE_OPTIONS, PERSONA_OPTIONS } from '../../../constants';
import styles from './Steps.module.css';

interface ValidationErrors {
  name?: string;
  email?: string;
  phone?: string;
  location?: string;
  whatsappNumber?: string;
}

export function PersonalDetailsStep() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const { formData, updatePersonalDetails, nextStep } = useFormStore();
  const { personalDetails } = formData;

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});


  const validateField = (field: string, value: string): string | undefined => {
    switch (field) {
      case 'name':
        if (value.trim().length < 2) return 'Please enter your full name';
        return undefined;
      case 'email':
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email';
        return undefined;
      case 'phone':
        const cleanPhone = value.replace(/\D/g, '');
        if (!/^[6-9]\d{9}$/.test(cleanPhone)) return 'Please enter a valid 10-digit mobile number';
        return undefined;
      case 'location':
        if (value.trim().length < 2) return 'Please enter your city or location';
        return undefined;
      case 'whatsappNumber':
        if (!personalDetails.whatsappSameAsPhone && value) {
          const cleanWa = value.replace(/\D/g, '');
          if (!/^[6-9]\d{9}$/.test(cleanWa)) return 'Please enter a valid 10-digit WhatsApp number';
        }
        return undefined;
      default:
        return undefined;
    }
  };

  const handleChange = (field: keyof typeof personalDetails, value: string | boolean) => {
    updatePersonalDetails({ [field]: value });
    if (touched[field as string] && typeof value === 'string') {
      setErrors(prev => ({ ...prev, [field]: validateField(field as string, value) }));
    }
  };

  const handleBlur = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const value = personalDetails[field as keyof typeof personalDetails];
    if (typeof value === 'string') {
      setErrors(prev => ({ ...prev, [field]: validateField(field, value) }));
    }
  };

  const handleNext = () => {
    const newErrors: ValidationErrors = {
      name: validateField('name', personalDetails.name),
      email: validateField('email', personalDetails.email),
      phone: validateField('phone', personalDetails.phone),
      location: validateField('location', personalDetails.location),
      whatsappNumber: !personalDetails.whatsappSameAsPhone
        ? validateField('whatsappNumber', personalDetails.whatsappNumber || '')
        : undefined,
    };

    setErrors(newErrors);
    setTouched({ name: true, email: true, phone: true, location: true, whatsappNumber: true });

    if (!Object.values(newErrors).some(error => error)) {
      nextStep();
    } 
  };

  return (
    <>
      <Card variant="elevated">
        <CardHeader>
          <CardTitle>Let's start with the basics</CardTitle>
          <CardDescription>Tell us about yourself so we can personalize your recommendation.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className={styles.formGrid}>
            <Input
              label="Full Name"
              placeholder="Enter your name"
              value={personalDetails.name}
              onChange={(e) => handleChange('name', e.target.value)}
              onBlur={() => handleBlur('name')}
              error={touched.name ? errors.name : undefined}
            />
            <Input
              label="Email Address"
              type="email"
              placeholder="you@example.com"
              value={personalDetails.email}
              onChange={(e) => handleChange('email', e.target.value)}
              onBlur={() => handleBlur('email')}
              error={touched.email ? errors.email : undefined}
              disabled
              hint="Verified from login"
            />
            <Input
              label="Mobile Number"
              type="tel"
              placeholder="10-digit mobile number"
              value={personalDetails.phone}
              onChange={(e) => handleChange('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
              onBlur={() => handleBlur('phone')}
              error={touched.phone ? errors.phone : undefined}
            />
            <Input
              label="City / Location"
              placeholder="e.g., Mumbai, Delhi, Bangalore"
              value={personalDetails.location}
              onChange={(e) => handleChange('location', e.target.value)}
              onBlur={() => handleBlur('location')}
              error={touched.location ? errors.location : undefined}
            />
          </div>

          <div className={styles.sectionTitle}>
            <h4>Your Age Group</h4>
            <p className={styles.sectionSubtitle}>Helps us understand your context better</p>
          </div>
          <div className={styles.optionsRow}>
            {AGE_OPTIONS.map(option => (
              <button
                key={option.value}
                type="button"
                className={`${styles.chipButton} ${personalDetails.age === option.value ? styles.chipActive : ''}`}
                onClick={() => handleChange('age', option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className={styles.sectionTitle}>
            <h4>Who are you?</h4>
            <p className={styles.sectionSubtitle}>Your primary role or occupation</p>
          </div>
          <div className={styles.optionsGrid}>
            {PERSONA_OPTIONS.map(option => (
              <SelectionCard
                key={option.value}
                selected={personalDetails.occupation === option.value}
                onClick={() => handleChange('occupation', option.value)}
                icon={<span className={styles.iconEmoji}>{option.icon}</span>}
                title={option.label}
              />
            ))}
          </div>

          <div className={styles.sectionTitle}>
            <h4>
              <MessageCircle size={18} className={styles.inlineIcon} />
              WhatsApp Contact
            </h4>
            <p className={styles.sectionSubtitle}>We'll send your personalized recommendation via WhatsApp</p>
          </div>

          <div className={styles.checkboxRow}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={personalDetails.whatsappSameAsPhone}
                onChange={(e) => handleChange('whatsappSameAsPhone', e.target.checked)}
              />
              <span>WhatsApp number is same as mobile number</span>
            </label>
          </div>

          {!personalDetails.whatsappSameAsPhone && (
            <div className={styles.conditionalField}>
              <Input
                label="WhatsApp Number"
                type="tel"
                placeholder="10-digit WhatsApp number"
                value={personalDetails.whatsappNumber || ''}
                onChange={(e) => handleChange('whatsappNumber', e.target.value.replace(/\D/g, '').slice(0, 10))}
                onBlur={() => handleBlur('whatsappNumber')}
                error={touched.whatsappNumber ? errors.whatsappNumber : undefined}
              />
            </div>
          )}
        </CardContent>
        <CardFooter>
          <Button onClick={handleNext} size="lg">
            Continue
            <ArrowRight size={18} />
          </Button>
        </CardFooter>
      </Card>
    </>
  );
}